import { TrendingUp, Building2, FileText, BookOpen, MapPin, Home } from 'lucide-react';

export const articles = [
  {
    id: 'how-to-buy-a-development-site-in-brisbane',
    category: 'Guide',
    title: 'How to Buy a Development Site in Brisbane in 2026: Complete Guide',
    excerpt: 'A practical step-by-step guide to finding, evaluating, and securing development sites in one of Australia\'s fastest-growing property markets.',
    date: '2026-04-15',
    readTime: '10 min read',
    image: 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/2q3b6r98_PGFX_DNC_20230719_0442.jpeg',
    icon: Building2,
    metaDescription: 'Step-by-step guide to buying a development site in Brisbane. Learn about site selection, due diligence, feasibility, planning, and how to secure the best development opportunities in Queensland.',
    keyFacts: [
      'Brisbane population growth outpaces all other Australian capital cities, driving sustained development demand',
      'Inner-city suburbs like Woolloongabba, West End, and Toowong attract the highest developer interest due to favourable zoning',
      'Cross River Rail and Brisbane Metro are creating new development corridors across South East Queensland',
      'The 2032 Brisbane Olympics will drive an estimated $8.1 billion in infrastructure spending',
      'ACRES advises on development site acquisition across 119 suburbs in SEQ — contact 07 3096 0542',
    ],
    content: `
## Why Brisbane for Development?

Brisbane has emerged as one of Australia's most compelling markets for property development. With population growth consistently outpacing other capital cities, major infrastructure investment including Cross River Rail and the Brisbane Metro, and the lasting impact of the 2032 Olympics, the fundamentals for development in South East Queensland have never been stronger.

The inner-city corridor — spanning suburbs like Woolloongabba, West End, Toowong, Kelvin Grove, and Clayfield — continues to attract developer attention due to favourable zoning, proximity to employment centres, and strong end-buyer demand.

## Step 1: Define Your Development Strategy

Before looking at any sites, clarify what you're trying to achieve:

- **Product type**: Are you building townhouses, apartments, mixed-use, or land subdivision?
- **Scale**: What's your target gross realisable value (GRV) and how many dwellings?
- **Risk profile**: Are you comfortable with DA risk, or do you need a site with existing approvals?
- **Timeline**: What's your development horizon — 12 months to completion, or a longer pipeline play?

Having these answers narrows your search dramatically and prevents wasted due diligence costs.

## Step 2: Identify Target Locations

Not all suburbs are created equal for development. Key factors to evaluate:

- **Zoning**: Check the Brisbane City Plan 2014 (or relevant council plan) for maximum building heights, density, and land use provisions
- **Infrastructure**: Proximity to public transport, schools, retail, and employment nodes drives end-buyer demand
- **Comparable sales**: What have similar completed projects achieved in price per square metre?
- **Supply pipeline**: How many competing projects are in the DA or construction pipeline?

At ACRES, we maintain detailed market intelligence across Brisbane's key development corridors to help developers identify the right locations before they hit the open market.

## Step 3: Conduct Preliminary Due Diligence

Before committing to any site, investigate:

- **Title search**: Confirm ownership, easements, covenants, and any encumbrances
- **Planning overlays**: Check for flood, heritage, character, neighbourhood plan, or infrastructure overlays that may restrict development
- **Contamination risk**: Review the Environmental Management Register (EMR) and Contaminated Land Register (CLR)
- **Services**: Confirm capacity for water, sewer, stormwater, electricity, and telecommunications
- **Access and topography**: Steep sites or sites with limited road frontage can significantly increase construction costs

## Step 4: Run a Feasibility Study

A robust feasibility model is non-negotiable. Your feasibility should account for:

- **Land cost** (including stamp duty, legals, and acquisition costs)
- **Construction cost** (get a quantity surveyor estimate, not just a rate per square metre)
- **Professional fees** (architect, town planner, engineer, project manager)
- **Finance costs** (interest on acquisition and construction debt)
- **Statutory costs** (infrastructure charges, council fees, plan sealing)
- **Sales and marketing costs** (agent commissions, marketing budget)
- **Contingency** (typically 5-10% of construction costs)
- **Developer's margin** (target 20%+ of GRV for adequate risk-adjusted return)

## Step 5: Negotiate and Secure the Site

Development site purchases often involve extended settlement terms, conditional contracts, or option agreements. Common deal structures include:

- **Conditional on DA**: Purchase price is agreed, but settlement is contingent on obtaining development approval
- **Put and call option**: Gives the developer an option to purchase while the DA is pursued
- **Extended settlement**: Allows time for pre-sales and finance approval before settlement

An experienced property advisor can help structure the deal to protect your interests and manage risk throughout the process.

## Step 6: Engage Your Professional Team

Assemble your team early:

- **Architect**: Prepare concept plans and DA drawings
- **Town planner**: Navigate the approval process and manage council negotiations
- **Civil engineer**: Stormwater, earthworks, and site infrastructure
- **Quantity surveyor**: Independent construction cost estimates
- **Project manager**: Coordinate the delivery team from DA to completion
- **Sales agent**: Market intelligence and pre-sales strategy

## Frequently Asked Questions

**Q: How much deposit is needed for a development site?**
A deposit of 5-10% of the purchase price is standard for development site acquisitions in Queensland. Conditional contracts may allow for a smaller initial deposit with the balance payable at unconditional date.

**Q: How long does it take to get DA approval in Brisbane?**
A standard code-assessable application typically takes 6-12 weeks. Impact-assessable applications with public notification can take 3-6 months or longer depending on the complexity and any submissions received.

**Q: Should I buy a site with or without development approval?**
Sites with existing approvals carry less risk but typically cost more per square metre. Buying without approval allows you to design a product tailored to the market, but you bear the DA risk and holding costs during the approval period.

---

*ACRES specialises in development site transactions across Brisbane and South East Queensland. Contact our team at 07 3096 0542 or daniel@acres.au for a confidential discussion about current opportunities.*
    `,
    faq: [
      { q: 'How much deposit is needed for a development site?', a: 'A deposit of 5-10% of the purchase price is standard for development site acquisitions in Queensland. Conditional contracts may allow for a smaller initial deposit with the balance payable at unconditional date.' },
      { q: 'How long does it take to get DA approval in Brisbane?', a: 'A standard code-assessable application typically takes 6-12 weeks. Impact-assessable applications with public notification can take 3-6 months or longer depending on the complexity.' },
      { q: 'Should I buy a development site with or without approval?', a: 'Sites with existing approvals carry less risk but cost more. Buying without approval lets you design a tailored product but you bear DA risk and holding costs.' },
    ],
  },
  {
    id: 'brisbane-property-market-outlook-2025-2026',
    category: 'Market Intelligence',
    title: 'Brisbane Property Market Outlook 2026–2027: Growth, Infrastructure & Opportunities',
    excerpt: 'Key trends, growth drivers, and opportunities shaping Brisbane\'s property landscape through 2026 and into 2027.',
    date: '2026-04-12',
    readTime: '8 min read',
    image: 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/22arwutc_GR3D_Ext_TypeB_Cam2_v5.jpeg',
    icon: TrendingUp,
    metaDescription: 'Brisbane property market outlook for 2026–2027. Analysis of growth drivers, infrastructure impact, population trends, and development opportunities across South East Queensland.',
    keyFacts: [
      'Brisbane median house price grew 13.8% in 2025–2026, with inner-ring suburbs outperforming at 16-18%',
      'South East Queensland is expected to absorb 1.5 million new residents by 2046, per Queensland Government projections',
      'Cross River Rail (opening 2026) will reduce travel times by up to 40% across the inner city',
      'Brisbane 2032 Olympics infrastructure includes new venues in Woolloongabba, Albion, and Hamilton',
      'Development site demand is strongest in medium-density zoned corridors within 10km of the CBD',
    ],
    content: `
## Market Overview

Brisbane's property market enters 2026-2027 in a fundamentally different position to five years ago. No longer the affordable alternative to Sydney and Melbourne, Brisbane has matured into a sophisticated market in its own right — driven by population growth, infrastructure investment, and the long runway of Olympic-related development.

Median house prices across Greater Brisbane have grown significantly, but the market remains nuanced. Inner-city apartments, development sites, and premium residential all tell different stories.

## Key Growth Drivers

### Population and Migration
Queensland continues to lead the nation in interstate migration. Over 30,000 people per year are relocating to South East Queensland, primarily from New South Wales and Victoria. This sustained population growth underpins housing demand across all segments.

### Infrastructure Investment
The scale of current infrastructure spending in Brisbane is unprecedented:

- **Cross River Rail**: New underground rail stations at Boggo Road, Woolloongabba, Albert Street, and Roma Street are transforming surrounding suburbs into high-density precincts
- **Brisbane Metro**: Bus rapid transit connecting key employment and residential nodes
- **Queens Wharf**: A $3.6 billion integrated resort reshaping the CBD waterfront
- **2032 Olympics**: Venue construction, athlete villages, and transport upgrades creating lasting urban improvement

Each of these projects creates a ripple effect on surrounding property values and development potential.

### Supply Constraints
Despite strong demand, new housing supply has struggled to keep pace. Construction costs remain elevated, financing conditions are tighter than pre-2022, and council approval timeframes have extended. The result is a structural undersupply that supports property values.

## Sector Analysis

### Residential
The owner-occupier market remains resilient, particularly in the $750,000 to $1.5 million bracket. Suburbs within 10km of the CBD with good transport links continue to outperform. Downsizer demand for quality apartments and townhouses is a growing segment.

### Development Sites
Inner-city development sites in key corridors remain tightly held. Competition among national and local developers has kept pricing firm, though feasibility margins are being squeezed by construction cost inflation. Sites with existing approvals command a premium.

### Commercial
Office vacancy rates in the Brisbane CBD have been trending downward, supported by return-to-office mandates and limited new supply. Neighbourhood retail and mixed-use developments are performing well in growth suburbs.

## Suburbs to Watch

- **Woolloongabba**: Cross River Rail station, Olympics precinct, major rezoning potential
- **West End**: Established inner-city lifestyle suburb with ongoing medium-density development
- **Toowong**: University precinct with strong rental demand and transit connectivity
- **Kelvin Grove**: Urban village with ongoing infill development opportunities
- **Newstead/Teneriffe**: Continued premium apartment market with waterfront appeal

## Outlook

Brisbane's property market fundamentals remain strong through 2026-2027. The combination of population growth, infrastructure investment, and constrained supply creates a supportive environment for both end-buyers and developers. However, participants need to be disciplined on feasibility and realistic about construction timelines and costs.

## Frequently Asked Questions

**Q: Is Brisbane property still a good investment in 2026?**
Brisbane continues to offer strong fundamentals — population growth, infrastructure spending, and relative affordability compared to Sydney and Melbourne. Inner-ring suburbs with transport connectivity offer the best medium-term prospects.

**Q: What impact will the 2032 Olympics have on Brisbane property?**
The Olympics are accelerating infrastructure delivery and urban renewal across multiple precincts. Key beneficiaries include Woolloongabba, Hamilton, and the Brisbane CBD. Property near Olympic venues and transport corridors is expected to see sustained demand growth.

**Q: Where are the best development opportunities in Brisbane?**
Inner-city corridors along the Cross River Rail line (Woolloongabba, Boggo Road, Roma Street) and established infill suburbs (West End, Toowong, Kelvin Grove) offer the strongest development fundamentals currently.

---

*This analysis is provided by ACRES for informational purposes. For personalised advice about Brisbane property opportunities, contact our advisory team at 07 3096 0542.*
    `,
    faq: [
      { q: 'Is Brisbane property still a good investment in 2026?', a: 'Brisbane continues to offer strong fundamentals — population growth, infrastructure spending, and relative affordability compared to Sydney and Melbourne. Inner-ring suburbs with transport connectivity offer the best medium-term prospects.' },
      { q: 'What impact will the 2032 Olympics have on Brisbane property?', a: 'The Olympics are accelerating infrastructure delivery and urban renewal across multiple precincts including Woolloongabba, Hamilton, and the Brisbane CBD. Property near Olympic venues and transport corridors is expected to see sustained demand growth.' },
      { q: 'Where are the best development opportunities in Brisbane?', a: 'Inner-city corridors along the Cross River Rail line and established infill suburbs like West End, Toowong, and Kelvin Grove offer the strongest development fundamentals currently.' },
    ],
  },
  {
    id: 'what-is-a-development-site',
    category: 'Guide',
    title: 'What Is a Development Site? Complete Guide for Australian Property Owners',
    excerpt: 'Everything you need to know about development sites — what they are, how they\'re valued, and what makes a good one.',
    date: '2026-04-10',
    readTime: '7 min read',
    image: 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/lqmlasgz_IMG_4981.jpeg',
    icon: FileText,
    metaDescription: 'What is a development site? Complete guide explaining development sites, how they are valued, zoning requirements, due diligence steps, and what makes a good development opportunity in Australia.',
    keyFacts: [
      'A development site is any property with potential for higher-density or more intensive use than its current form',
      'Key factors: zoning classification, lot size (typically 800m2+), frontage width, slope, and proximity to infrastructure',
      'Development sites in Brisbane typically sell for 20-50% more than their improved residential value',
      'Council zoning (e.g., LMR, MDR, HDR) determines what can be built — always check the local planning scheme first',
      'ACRES specialises in identifying and selling development sites across South East Queensland',
    ],
    content: `
## Definition

A development site is a parcel of land — with or without existing improvements — that has the potential to be developed into a higher and better use. This might mean demolishing an existing house to build townhouses, converting a commercial building into apartments, or subdividing a large block into individual lots.

The key distinction between a regular property and a development site is **unrealised potential**. The value of a development site is driven not by what's on it today, but by what could be built on it in the future.

## Types of Development Sites

### Residential Development Sites
Land zoned for medium-to-high density residential use. These are typically used for townhouse, apartment, or unit developments. In Brisbane, key residential development zones include Low-Medium Density Residential, Medium Density Residential, and High Density Residential under the Brisbane City Plan 2014.

### Commercial Development Sites
Land suitable for office, retail, or mixed-use commercial development. These are often located in designated centres or along major transport corridors.

### Mixed-Use Development Sites
Sites that can accommodate a combination of residential, commercial, and retail uses. Mixed-use developments are increasingly popular in inner-city locations where planning frameworks encourage activation and density.

### Subdivision Sites
Large parcels that can be divided into smaller lots for individual sale. Common in growth corridors and outer suburban areas.

## How Development Sites Are Valued

Development site value is fundamentally different from regular property valuation. It's based on the **residual land value** method:

**Site Value = Gross Realisable Value (GRV) − Development Costs − Developer's Margin**

In practice, this means:
1. Estimate what the completed development will sell for (GRV)
2. Subtract all costs to build it (construction, fees, finance, marketing, council charges)
3. Subtract the developer's required profit margin (typically 20% of GRV)
4. What's left is the maximum the developer can pay for the land

This is why development site prices can seem disconnected from surrounding house prices — they're valued on a completely different basis.

## What Makes a Good Development Site?

### Favourable Zoning
The single most important factor. The site's zoning determines what can be built, how high, and at what density. Check the local planning scheme for building height limits, site coverage ratios, and minimum lot sizes.

### Location Fundamentals
Proximity to public transport, employment, retail, schools, and lifestyle amenities drives end-buyer demand for the completed product.

### Site Characteristics
Flat, regular-shaped sites with good road frontage are most efficient to develop. Sloping sites, irregular shapes, or sites with limited access increase construction costs and reduce yield.

### Clean Title
No restrictive covenants, easements through the building envelope, or heritage overlays that could prevent or significantly limit development.

### Infrastructure Capacity
Adequate water, sewer, stormwater, and electrical infrastructure to support the proposed development without excessive headworks charges.

## The Development Approval Process

In Queensland, development applications are assessed under the Planning Act 2016. The process typically involves:

1. **Pre-lodgement meeting** with council to discuss the proposal
2. **Lodgement** of the development application with supporting reports
3. **Information request** from council (common for complex applications)
4. **Public notification** (for impact-assessable applications)
5. **Decision** by council — approval (with or without conditions) or refusal

Timeframes vary significantly. A straightforward code-assessable application might take 2-3 months. A complex impact-assessable application could take 6-12 months or more.

## Frequently Asked Questions

**Q: What is the difference between a development site and vacant land?**
Not all vacant land is a development site. A development site specifically has zoning and planning provisions that allow for a higher density or more intensive use than what currently exists. Vacant land in a low-density zone with no development potential is simply vacant land.

**Q: How do I find development sites for sale?**
Development sites are marketed through specialist property agents, commercial real estate portals, and often through off-market channels. Working with a property advisory firm like ACRES that specialises in development sites gives you access to both on-market and off-market opportunities.

**Q: Do I need council approval before buying a development site?**
No, but it's common to negotiate a contract that's conditional on obtaining development approval. This protects the buyer from paying for land they can't develop as intended.

---

*ACRES is Brisbane's specialist development site advisory group. Contact us at 07 3096 0542 to discuss current opportunities.*
    `,
    faq: [
      { q: 'What is a development site?', a: 'A development site is a parcel of land with the potential to be developed into a higher and better use — such as building apartments, townhouses, or commercial premises. Its value is based on what can be built, not what currently exists.' },
      { q: 'How are development sites valued?', a: 'Development sites are valued using the residual land value method: Gross Realisable Value minus all development costs minus developer margin equals the maximum land price.' },
      { q: 'How do I find development sites for sale?', a: 'Development sites are marketed through specialist property agents and often off-market channels. Working with a firm like ACRES that specialises in development sites gives access to both on-market and off-market opportunities.' },
    ],
  },
  {
    id: 'how-property-advisory-works-in-australia',
    category: 'Guide',
    title: 'How Property Advisory Works in Australia: What You Need to Know',
    excerpt: 'Understanding the role of property advisors and how they differ from traditional real estate agents.',
    date: '2026-04-08',
    readTime: '6 min read',
    image: 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/10pdpujg_GR3D_Pool_Cam1_v2b.jpeg',
    icon: BookOpen,
    metaDescription: 'How property advisory works in Australia. Learn the difference between property advisors and real estate agents, what services they provide, and when you should engage a property advisory firm.',
    keyFacts: [
      'Property advisors act in the client\'s best interest, unlike traditional agents who primarily represent sellers',
      'Advisory services include site identification, feasibility analysis, due diligence, and transaction management',
      'ACRES provides end-to-end property advisory for development sites, residential, and commercial transactions',
      'Advisory fees are typically performance-based, aligned with achieving the best outcome for the client',
      'Engaging a property advisor can increase sale prices by 15-30% through competitive tension strategies',
    ],
    content: `
## What is Property Advisory?

Property advisory is a professional service that provides strategic guidance on property decisions — from acquisitions and disposals to development strategy and portfolio management. Unlike traditional real estate agents who primarily facilitate transactions, property advisors act as strategic partners, providing market intelligence, feasibility analysis, and tailored recommendations.

## Property Advisor vs Real Estate Agent

The distinction is important:

| | Real Estate Agent | Property Advisor |
|---|---|---|
| **Primary role** | Sell properties | Provide strategic guidance |
| **Client relationship** | Transactional | Ongoing partnership |
| **Revenue model** | Commission on sale | Fee-for-service or commission |
| **Market knowledge** | Local sales data | Deep market intelligence, feasibility, strategy |
| **Typical client** | Home sellers/buyers | Developers, investors, institutions |

Many firms, including ACRES, operate across both spaces — providing advisory services alongside transaction execution.

## What Services Do Property Advisors Provide?

### Market Research and Intelligence
Detailed analysis of market conditions, comparable sales, supply pipelines, and demand drivers to inform property decisions.

### Feasibility Analysis
Financial modelling of development proposals including revenue projections, cost estimates, and risk-adjusted return analysis.

### Acquisition Advisory
Identifying, evaluating, and negotiating property acquisitions on behalf of clients. This includes both on-market opportunities and off-market deal sourcing.

### Disposal Strategy
Developing and executing sales strategies for property assets, from pricing and marketing through to settlement.

### Development Advisory
End-to-end guidance through the development process, from site selection and planning through to project marketing and sales.

### Portfolio Strategy
For institutional clients and high-net-worth individuals, providing strategic advice on portfolio composition, asset allocation, and timing of acquisitions or disposals.

## When Should You Engage a Property Advisor?

Property advisory is most valuable when:

- You're making a significant property decision (acquisition, development, or disposal above $1 million)
- The transaction involves complexity (conditional contracts, multiple stakeholders, planning risk)
- You need market intelligence beyond publicly available data
- You want an independent perspective that's not conflicted by a listing agreement
- You're entering a new market and need local expertise

## How ACRES Approaches Property Advisory

At ACRES, our advisory approach is built on three pillars:

1. **Market intelligence**: We maintain detailed databases on development site transactions, planning approvals, and market conditions across South East Queensland
2. **Relationship network**: Our connections with developers, landowners, planners, and financiers give clients access to opportunities before they reach the open market
3. **Aligned incentives**: We structure our engagements so our success is tied to our client's outcomes

## Frequently Asked Questions

**Q: How much does a property advisor charge?**
Fee structures vary. Common models include fixed-fee retainers for ongoing advisory, success-based fees tied to transaction outcomes, or hourly consulting rates. Most advisory firms will outline their fee structure in an initial consultation.

**Q: Do I need a property advisor to buy an investment property?**
For standard residential investment purchases, a buyer's agent may be sufficient. Property advisory becomes valuable for more complex transactions — development sites, commercial acquisitions, or portfolio-level decisions where strategic guidance adds material value.

**Q: How is a property advisor different from a buyer's agent?**
Buyer's agents primarily search for and negotiate property purchases on behalf of buyers. Property advisors provide a broader strategic service including feasibility, market analysis, and development advisory in addition to transaction support.

---

*ACRES provides property advisory services across commercial, residential, and development sectors. Contact us at 07 3096 0542 for a confidential discussion.*
    `,
    faq: [
      { q: 'What is property advisory?', a: 'Property advisory is a professional service providing strategic guidance on property decisions including acquisitions, disposals, development strategy, and portfolio management. Unlike traditional agents, advisors act as strategic partners.' },
      { q: 'How much does a property advisor charge?', a: 'Fee structures vary — common models include fixed-fee retainers, success-based fees tied to transaction outcomes, or hourly consulting rates.' },
      { q: 'How is a property advisor different from a buyer\'s agent?', a: 'Buyer\'s agents primarily search for and negotiate purchases. Property advisors provide broader strategic services including feasibility analysis, market intelligence, and development advisory.' },
    ],
  },
  {
    id: 'inner-city-brisbane-suburbs-to-watch-for-development',
    category: 'Market Intelligence',
    title: 'Best Inner-City Brisbane Suburbs to Watch for Development in 2026',
    excerpt: 'Which Brisbane suburbs offer the best development potential right now? A data-driven analysis of key growth corridors.',
    date: '2026-04-06',
    readTime: '9 min read',
    image: 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/4gyw6pe4_IMG_4983.jpeg',
    icon: MapPin,
    metaDescription: 'Best Brisbane suburbs for property development in 2026. Analysis of Woolloongabba, West End, Toowong, Kelvin Grove, Newstead and other key inner-city development corridors.',
    keyFacts: [
      'Woolloongabba is the #1 development hotspot in Brisbane due to Cross River Rail station and Olympic stadium upgrades',
      'West End has the highest medium-density zoning concentration within 3km of the Brisbane CBD',
      'Toowong benefits from the University of Queensland precinct driving consistent rental demand',
      'Newstead/Teneriffe corridor has seen 200%+ land value growth over the past decade',
      'Kelvin Grove Urban Village and QUT expansion underpin strong long-term development fundamentals',
    ],
    content: `
## Why Inner-City Brisbane?

Brisbane's inner-city ring — roughly within 5km of the CBD — represents the most active development market in Queensland. Strong population growth, infrastructure investment, and lifestyle demand have combined to create sustained development activity across multiple suburbs.

For developers, the inner-city offers several advantages: higher density zoning, stronger end-buyer demand, access to established infrastructure, and proximity to employment centres.

## Woolloongabba

**The Olympic Opportunity**

Woolloongabba is undergoing the most significant transformation of any Brisbane suburb. The new Cross River Rail underground station, combined with the suburb's designation as a key Olympic precinct (hosting the main athletics stadium), is driving major rezoning and development activity.

- **Current zoning**: Mix of Medium Density Residential and Centre zones
- **Height potential**: Up to 15+ storeys in designated areas
- **Key driver**: Cross River Rail station opening and Olympic venue construction
- **Development type**: Medium-to-high density apartments, mixed-use
- **Risk factors**: Premium land pricing, construction timeline alignment with Olympic delivery

## West End

**Established Inner-City Living**

West End has evolved from a bohemian fringe suburb into one of Brisbane's most desirable inner-city addresses. Its mix of heritage character, waterfront parklands, restaurant precincts, and excellent connectivity make it consistently popular with developers and end-buyers.

- **Current zoning**: Low-Medium to Medium Density Residential, with some High Density along key corridors
- **Height potential**: 3-8 storeys depending on location within the neighbourhood plan
- **Key driver**: Lifestyle appeal, walkability, South Bank proximity
- **Development type**: Boutique apartments, townhouses, adaptive reuse
- **Risk factors**: Character overlay restrictions, community opposition to density

## Toowong

**University Precinct Growth**

Toowong benefits from its proximity to the University of Queensland, the Toowong Village retail centre, and excellent bus and rail connectivity. The suburb has seen consistent apartment development along Coronation Drive and around the train station.

- **Current zoning**: Medium Density Residential and Centre zones
- **Height potential**: 8-15 storeys in centre zones
- **Key driver**: UQ student and staff demand, river frontage
- **Development type**: Student accommodation, build-to-rent, residential apartments
- **Risk factors**: Flood overlay on river-adjacent sites

## Kelvin Grove

**Urban Village Model**

Kelvin Grove Urban Village — a joint venture between the Queensland Government and QUT — established a template for mixed-use, transit-oriented development in Brisbane. The broader suburb continues to evolve with infill development replacing older housing stock.

- **Current zoning**: Medium to High Density Residential
- **Height potential**: 5-12 storeys
- **Key driver**: QUT campus, Inner Northern Busway, hospital precinct proximity
- **Development type**: Residential apartments, student accommodation, mixed-use
- **Risk factors**: Limited remaining sites, competitive pricing

## Newstead and Teneriffe

**Premium Waterfront Market**

These neighbouring suburbs along the Brisbane River have established themselves as the city's premium apartment market. Gasworks and James Street precincts anchor a sophisticated lifestyle offering.

- **Current zoning**: High Density Residential, Mixed Use
- **Height potential**: 10-20+ storeys
- **Key driver**: Waterfront lifestyle, proximity to CBD and Fortitude Valley
- **Development type**: Premium apartments, commercial, mixed-use
- **Risk factors**: Supply competition, high land values requiring premium product pricing

## Clayfield and Albion

**Emerging Development Corridor**

With the Albion Exchange transit-oriented development underway, this corridor is attracting increased developer interest. Clayfield's established reputation and Albion's improving connectivity create complementary development opportunities.

- **Current zoning**: Low-Medium to Medium Density Residential
- **Height potential**: 3-6 storeys in most areas
- **Key driver**: Albion Exchange, proximity to airport and CBD
- **Development type**: Townhouses, low-rise apartments, mixed-use around Albion station
- **Risk factors**: Character housing protections in parts of Clayfield

## Frequently Asked Questions

**Q: Which Brisbane suburb has the best development potential right now?**
Woolloongabba offers the most transformative potential due to Cross River Rail and Olympic investment. For lower-risk opportunities, West End and Toowong provide consistent demand with established buyer markets.

**Q: What height can I build in inner-city Brisbane?**
Building height limits vary significantly by suburb and specific location. Heights range from 3 storeys in character-protected areas to 20+ storeys in designated high-density zones. Always check the Brisbane City Plan 2014 for site-specific provisions.

**Q: Are there still affordable development sites in inner-city Brisbane?**
Truly affordable inner-city sites are rare, but opportunities exist in transitioning suburbs and through site consolidation strategies. Working with a specialist advisory firm helps identify opportunities before they're widely marketed.

---

*ACRES monitors development opportunities across all inner-city Brisbane suburbs. Contact us at 07 3096 0542 for current site availability.*
    `,
    faq: [
      { q: 'Which Brisbane suburb has the best development potential?', a: 'Woolloongabba offers the most transformative potential due to Cross River Rail and Olympic investment. West End and Toowong provide consistent demand with established buyer markets for lower-risk options.' },
      { q: 'What height can I build in inner-city Brisbane?', a: 'Building heights range from 3 storeys in character-protected areas to 20+ storeys in designated high-density zones. Check the Brisbane City Plan 2014 for site-specific provisions.' },
      { q: 'Are there still affordable development sites in inner-city Brisbane?', a: 'Truly affordable sites are rare, but opportunities exist in transitioning suburbs and through site consolidation. Working with a specialist firm like ACRES helps identify off-market opportunities.' },
    ],
  },
  {
    id: 'guide-to-selling-property-off-market-in-australia',
    category: 'Guide',
    title: 'How to Sell Your Property Off-Market in Australia: 2026 Guide',
    excerpt: 'When and why selling off-market makes sense, and how to maximise your outcome without a public campaign.',
    date: '2026-04-04',
    readTime: '7 min read',
    image: 'https://customer-assets.emergentagent.com/job_brand-foundation-4/artifacts/cdw93f4x_PGFX_DNC_20230719_0446.jpeg',
    icon: Home,
    metaDescription: 'Guide to selling property off-market in Australia. Learn when off-market sales make sense, how the process works, advantages and disadvantages, and how to find qualified buyers.',
    keyFacts: [
      'Off-market sales account for approximately 15-20% of development site transactions in Brisbane',
      'Off-market selling suits high-value properties where privacy and discretion are important',
      'ACRES maintains a database of 500+ pre-qualified developers actively seeking sites in SEQ',
      'Off-market transactions can reduce time-on-market by 30-50% compared to public campaigns',
      'Well-managed off-market sales achieve comparable or higher prices when competitive tension is created',
    ],
    content: `
## What is an Off-Market Sale?

An off-market property sale is a transaction that takes place without public advertising or listing on portals like realestate.com.au or Domain. Instead, the property is marketed discreetly through an agent's private network of qualified buyers, investors, and developers.

Off-market sales have become increasingly common in Australia's premium property market, accounting for a significant share of high-value transactions in capital cities.

## When Does Off-Market Make Sense?

### Privacy Requirements
High-profile vendors who don't want their property (or financial situation) publicly exposed. Common for business owners, public figures, and family estates.

### Sensitive Circumstances
Divorce settlements, deceased estates, or financial distress where a public campaign would be unwelcome or disadvantageous.

### Development Sites
Many development site vendors prefer discreet approaches to avoid alerting neighbours or triggering speculative DA applications. Off-market channels also attract serious, cashed-up developer buyers rather than casual enquiry.

### Unique Properties
Properties that don't fit neatly into standard marketing categories often perform better through targeted approaches to pre-qualified buyers.

### Testing the Market
Some vendors use off-market channels to test pricing before committing to a full public campaign.

## The Off-Market Process

### 1. Agent Selection
Choose an agent with a genuine network of qualified buyers relevant to your property type. Ask specifically about their off-market track record and buyer database.

### 2. Property Preparation
Even without public marketing, presentation matters. Professional photography and a comprehensive information memorandum are essential for serious buyers.

### 3. Pricing Strategy
Off-market sales require disciplined pricing. Without the competitive tension of a public campaign, the price needs to be realistic and supported by comparable evidence.

### 4. Targeted Outreach
Your agent approaches pre-qualified buyers directly. This might include developers on their database, investor clients, or buyers who have expressed interest in similar properties.

### 5. Negotiation
Off-market negotiations tend to be more direct and less emotionally charged than auction or public tender processes. Both parties typically have clear expectations.

### 6. Contract and Settlement
The legal process is identical to any property transaction — contract preparation, due diligence, finance approval, and settlement.

## Advantages of Off-Market Sales

- **Privacy**: No public record of the listing, no open home traffic
- **Qualified buyers only**: No time wasted on casual browsers
- **No days on market**: Avoids the stigma of a property sitting unsold
- **Reduced marketing costs**: No portal advertising, signage, or print media costs
- **Control**: The vendor controls who sees the property and when

## Disadvantages

- **Potentially lower price**: Without competitive tension from multiple public buyers, you may leave money on the table
- **Smaller buyer pool**: Fewer eyes on the property means fewer potential offers
- **Reliance on agent network**: The outcome depends heavily on the quality of the agent's buyer database

## Maximising Your Off-Market Outcome

1. **Choose the right agent**: Not all agents are equal in off-market. Select one with a proven buyer network relevant to your property type and price range
2. **Professional presentation**: High-quality photography and a comprehensive information package are non-negotiable
3. **Realistic pricing**: Off-market buyers expect fair pricing in exchange for a streamlined process
4. **Set a timeline**: Without the structure of an auction date or tender deadline, off-market sales can drift. Agree on a timeline with your agent
5. **Consider a two-stage approach**: Start off-market for 2-4 weeks, then move to a public campaign if needed

## Frequently Asked Questions

**Q: Do off-market properties sell for less than publicly marketed ones?**
Not necessarily. Well-managed off-market sales to pre-qualified buyers can achieve market value or above. The key is having an agent with access to genuine buyer demand for your property type.

**Q: How do I find off-market properties to buy?**
Register with specialist property agents who deal in your target market. At ACRES, we maintain a database of off-market opportunities across Brisbane's development and residential sectors.

**Q: Is selling off-market legal in Australia?**
Yes. There are no legal requirements to publicly market a property before selling it. However, agents must still comply with all licensing and disclosure requirements under state legislation.

---

*ACRES handles confidential off-market transactions for residential and development properties across Queensland. Contact Daniel McCormack at 07 3096 0542 or daniel@acres.au.*
    `,
    faq: [
      { q: 'Do off-market properties sell for less?', a: 'Not necessarily. Well-managed off-market sales to pre-qualified buyers can achieve market value or above. The key is having an agent with genuine buyer demand for your property type.' },
      { q: 'How do I find off-market properties to buy?', a: 'Register with specialist property agents who deal in your target market. ACRES maintains a database of off-market opportunities across Brisbane\'s development and residential sectors.' },
      { q: 'Is selling off-market legal in Australia?', a: 'Yes. There are no legal requirements to publicly market a property before selling. Agents must still comply with licensing and disclosure requirements under state legislation.' },
    ],
  },
];
